import React, { useRef } from 'react';
import { Box, Container, Typography } from '@mui/material';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import styled from 'styled-components';
import SearchIcon from '@mui/icons-material/Search';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import BarChartIcon from '@mui/icons-material/BarChart';
import DataObjectIcon from '@mui/icons-material/DataObject';
import RouteIcon from '@mui/icons-material/Route';
import SecurityIcon from '@mui/icons-material/Security';
import ScaleIcon from '@mui/icons-material/Scale';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import VerifiedIcon from '@mui/icons-material/Verified';
import SyncIcon from '@mui/icons-material/Sync';
import MonitorIcon from '@mui/icons-material/Monitor';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import UpdateIcon from '@mui/icons-material/Update';

interface StyledProps {
  isLeft?: boolean;
}

const ApproachSection = styled(Box)`
  padding: 140px 0;
  position: relative;
  background: linear-gradient(135deg, #030b17 0%, #010a14 100%);
  overflow-x: hidden;
  min-height: 100vh;
  width: 100%;
  max-width: 100vw;

  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const ParticleCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.3;
  pointer-events: none;
`;

const BackgroundLines = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
`;

const GlowingCircle = styled(motion.div)`
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(79, 172, 254, 0.1) 0%, transparent 70%);
  z-index: 0;
  filter: blur(60px);
`;

const SectionContent = styled(Box)`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 4rem;
  font-weight: 700;
  color: #f5f5f5;
  margin-bottom: 1.5rem;
  text-align: center;
  background: linear-gradient(to right, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 6rem;
  max-width: 800px;
  text-align: center;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.8;
  padding: 0 2rem;
`;

const TimelineProgress = styled(motion.div)`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(to bottom, #4facfe, #00f2fe);
  z-index: -1;
  transform-origin: top;
  left: 50%;
  transform: translateX(-50%);
  filter: drop-shadow(0 0 10px rgba(79, 172, 254, 0.5));

  &::after {
    content: '';
    position: absolute;
    width: 15px;
    height: 15px;
    background: #4facfe;
    border-radius: 50%;
    left: 50%;
    bottom: 0;
    transform: translate(-50%, 50%);
    box-shadow: 0 0 20px rgba(79, 172, 254, 0.8);
  }
`;

const ApproachTimeline = styled(Box)`
  position: relative;
  padding: 4rem 0;
  max-width: 1200px;
  margin: 0 auto;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 2px;
    background: rgba(79, 172, 254, 0.1);
    z-index: -1;
    left: 50%;
    transform: translateX(-50%);
  }

  @media (max-width: 768px) {
    padding: 2rem 0;
    
    &::before {
      display: none;
    }
  }
`;

const TechPattern = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  opacity: 0.1;
  background: 
    linear-gradient(90deg, transparent 50%, rgba(79, 172, 254, 0.1) 50%) 0 0 / 4px 4px,
    radial-gradient(circle at 50% 50%, rgba(0, 242, 254, 0.2) 0%, transparent 1px) 0 0 / 20px 20px;
  filter: blur(0.5px);
`;

const CircuitLine = styled(motion.div)`
  position: absolute;
  background: linear-gradient(90deg, rgba(79, 172, 254, 0.1), rgba(0, 242, 254, 0.1));
  height: 1px;
  width: 100px;
  opacity: 0.5;

  &::before {
    content: '';
    position: absolute;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: rgba(79, 172, 254, 0.3);
    top: 50%;
    transform: translateY(-50%);
  }

  &.right {
    right: 0;
    &::before {
      left: -2px;
    }
  }

  &.left {
    left: 0;
    &::before {
      right: -2px;
    }
  }
`;

const FloatingElement = styled(motion.div)`
  position: absolute;
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: rgba(79, 172, 254, 0.1);
  border: 1px solid rgba(79, 172, 254, 0.2);
  backdrop-filter: blur(5px);
`;

const TimelineItem = styled(Box)`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  margin-bottom: 8rem;
  min-height: 300px;
  z-index: 1;
  
  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    margin-bottom: 4rem;
    min-height: auto;
    padding: 0 1rem;
  }
`;

const TimelineConnector = styled(motion.div)<StyledProps>`
  position: absolute;
  height: 3px;
  background: linear-gradient(
    ${({ isLeft }) => isLeft ? 'to left' : 'to right'},
    rgba(79, 172, 254, 0.8),
    rgba(79, 172, 254, 0.1)
  );
  width: 80px;
  top: 50%;
  ${({ isLeft }) => isLeft ? 'right: -80px' : 'left: -80px'};
  transform: translateY(-50%);
  z-index: 1;
  filter: drop-shadow(0 0 5px rgba(79, 172, 254, 0.3));

  @media (max-width: 768px) {
    display: none;
  }
`;

const IconContainer = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 2;

  @media (max-width: 768px) {
    position: static;
    transform: none;
    margin: 2rem auto;
    order: 0;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const TimelineDecoration = styled(motion.div)`
  position: absolute;
  width: 200px;
  height: 200px;
  opacity: 0.05;
  background: radial-gradient(circle, rgba(79, 172, 254, 0.2) 0%, transparent 70%);
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
  
  &.left {
    left: 5%;
  }
  
  &.right {
    right: 5%;
  }
`;

const TimelinePattern = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-image: 
    radial-gradient(circle at 20% 30%, rgba(79, 172, 254, 0.03) 0%, transparent 8%),
    radial-gradient(circle at 80% 60%, rgba(79, 172, 254, 0.03) 0%, transparent 8%);
  pointer-events: none;
  z-index: 0;
`;

const TimelineIcon = styled(motion.div)`
  width: 100px;
  height: 100px;
  border-radius: 30px;
  background: rgba(10, 25, 41, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(79, 172, 254, 0.3);
  box-shadow: 
    0 0 30px rgba(79, 172, 254, 0.2),
    inset 0 0 30px rgba(79, 172, 254, 0.1);
  backdrop-filter: blur(10px);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(45deg);
  
  svg {
    color: #4facfe;
    font-size: 45px;
    filter: drop-shadow(0 0 10px rgba(79, 172, 254, 0.5));
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    transform: rotate(-45deg);
  }

  &:hover {
    transform: rotate(45deg) scale(1.1);
    border-color: rgba(79, 172, 254, 0.6);
    box-shadow: 
      0 0 40px rgba(79, 172, 254, 0.4),
      inset 0 0 40px rgba(79, 172, 254, 0.2);
    
    svg {
      transform: rotate(-45deg) scale(1.2);
      filter: drop-shadow(0 0 20px rgba(79, 172, 254, 0.8));
    }
  }
`;

const TimelineContent = styled(motion.div)`
  width: calc(50% - 80px);
  background: rgba(10, 25, 41, 0.7);
  border-radius: 30px;
  padding: 3rem;
  border: 1px solid rgba(79, 172, 254, 0.2);
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 0 30px rgba(79, 172, 254, 0.05);
  backdrop-filter: blur(10px);
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: left;
  margin-top: 0;
  overflow: hidden;
  z-index: 2;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(79, 172, 254, 0.2) 50%,
      transparent 100%
    );
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(0, 242, 254, 0.2) 50%,
      transparent 100%
    );
  }
  
  &:hover {
    border-color: rgba(79, 172, 254, 0.4);
    box-shadow: 
      0 30px 60px rgba(0, 0, 0, 0.4),
      inset 0 0 50px rgba(79, 172, 254, 0.1);
    transform: translateY(-5px);
  }

  @media (max-width: 768px) {
    width: 100%;
    margin: 1rem 0;
    text-align: center;
  }
`;

const TimelineContentLeft = styled(TimelineContent)`
  margin-right: auto;

  @media (max-width: 768px) {
    width: 100%;
    margin: 0;
    order: 1;
    margin-bottom: 2rem;
  }
`;

const TimelineContentRight = styled(TimelineContent)`
  margin-left: auto;

  @media (max-width: 768px) {
    width: 100%;
    margin: 0;
    order: 1;
    margin-bottom: 2rem;
  }
`;

const TimelineTitle = styled(Typography)<StyledProps>`
  font-size: 2.5rem;
  font-weight: 700;
  color: #f5f5f5;
  margin-bottom: 4rem;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: left;
  letter-spacing: -0.5px;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    text-align: center;
    margin-bottom: 2rem;
  }
`;

const TimelineDescription = styled(Typography)`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.8;
  margin: 0;
  margin-top: 2rem;
  margin-bottom: 3rem;
  letter-spacing: 0.2px;

  @media (max-width: 768px) {
    text-align: center;
    margin-bottom: 2rem;
  }
`;

const Metric = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(79, 172, 254, 0.2);
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.2rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  span {
    font-size: 2.5rem;
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 700;
    letter-spacing: -1px;
  }
`;

const ServiceList = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 2rem;
`;

const ServiceItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem;
  background: rgba(10, 25, 41, 0.8);
  border-radius: 16px;
  border: 1px solid rgba(79, 172, 254, 0.2);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(79, 172, 254, 0.4);
    transform: translateX(10px);
    box-shadow: 0 4px 30px rgba(79, 172, 254, 0.2);
  }
`;

const ServiceIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(79, 172, 254, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(79, 172, 254, 0.2);
  
  svg {
    color: #4facfe;
    font-size: 24px;
    filter: drop-shadow(0 0 8px rgba(79, 172, 254, 0.5));
  }
`;

const ServiceText = styled.div`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  letter-spacing: 0.5px;
`;

const ServiceListRight = styled(motion.div)`
  position: absolute;
  right: 60px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 380px;
  z-index: 2;

  @media (max-width: 768px) {
    position: static;
    transform: none;
    width: 100%;
    padding: 1rem;
    margin-top: 2rem;
    order: 2;
    align-items: center;
  }
`;

const ServiceItemRight = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: rgba(10, 25, 41, 0.8);
  border-radius: 12px;
  border: 1px solid rgba(79, 172, 254, 0.2);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  position: relative;
  min-width: 360px;

  &::before {
    content: '';
    position: absolute;
    left: -8px;
    top: 50%;
    width: 8px;
    height: 1px;
    background: linear-gradient(to right, transparent, rgba(79, 172, 254, 0.3));
  }

  &:hover {
    border-color: rgba(79, 172, 254, 0.4);
    transform: translateX(-5px);
    box-shadow: 0 4px 30px rgba(79, 172, 254, 0.2);

    &::before {
      background: linear-gradient(to right, transparent, rgba(79, 172, 254, 0.5));
    }
  }

  @media (max-width: 768px) {
    min-width: 100%;
    max-width: 360px;
    margin-bottom: 1rem;
    
    &::before {
      display: none;
    }

    &:hover {
      transform: none;
    }
  }
`;

const ServiceIconRight = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    color: #4facfe;
    font-size: 20px;
    filter: drop-shadow(0 0 8px rgba(79, 172, 254, 0.5));
  }
`;

const ServiceTextRight = styled.div`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
  white-space: nowrap;
`;

const ServiceListLeft = styled(motion.div)`
  position: absolute;
  left: 60px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 380px;
  z-index: 2;

  @media (max-width: 768px) {
    position: static;
    transform: none;
    width: 100%;
    padding: 1rem;
    margin-top: 2rem;
    order: 2;
    align-items: center;
  }
`;

const ServiceItemLeft = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: rgba(10, 25, 41, 0.8);
  border-radius: 12px;
  border: 1px solid rgba(79, 172, 254, 0.2);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  position: relative;
  min-width: 360px;

  &::before {
    content: '';
    position: absolute;
    right: -8px;
    top: 50%;
    width: 8px;
    height: 1px;
    background: linear-gradient(to left, transparent, rgba(79, 172, 254, 0.3));
  }

  &:hover {
    border-color: rgba(79, 172, 254, 0.4);
    transform: translateX(5px);
    box-shadow: 0 4px 30px rgba(79, 172, 254, 0.2);

    &::before {
      background: linear-gradient(to left, transparent, rgba(79, 172, 254, 0.5));
    }
  }

  @media (max-width: 768px) {
    min-width: 100%;
    max-width: 360px;
    margin-bottom: 1rem;
    
    &::before {
      display: none;
    }

    &:hover {
      transform: none;
    }
  }
`;

const MobileContainer = styled(Box)`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    padding: 2rem 1rem;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
  }
`;

interface ParticleType {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  update: () => void;
  draw: (ctx: CanvasRenderingContext2D) => void;
}

class Particle implements ParticleType {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;

  constructor(width: number, height: number) {
    this.x = Math.random() * width;
    this.y = Math.random() * height;
    this.size = Math.random() * 2;
    this.speedX = Math.random() * 0.5 - 0.25;
    this.speedY = Math.random() * 0.5 - 0.25;
  }

  update() {
    this.x += this.speedX;
    this.y += this.speedY;

    if (this.x > window.innerWidth) this.x = 0;
    if (this.x < 0) this.x = window.innerWidth;
    if (this.y > window.innerHeight) this.y = 0;
    if (this.y < 0) this.y = window.innerHeight;
  }

  draw(ctx: CanvasRenderingContext2D) {
    ctx.fillStyle = 'rgba(79, 172, 254, 0.1)';
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
    ctx.fill();
  }
}

const Approach = () => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start center", "end center"]
  });
  
  const scaleY = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  React.useEffect(() => {
    const canvas = document.getElementById('particle-canvas') as HTMLCanvasElement;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const ctx: CanvasRenderingContext2D = context;

    const particles: ParticleType[] = [];
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    function init() {
      for (let i = 0; i < 100; i++) {
        particles.push(new Particle(canvas.width, canvas.height));
      }
    }

    let animationId: number;

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(particle => {
        particle.update();
        particle.draw(ctx);
      });
      animationId = requestAnimationFrame(animate);
    }

    init();
    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, []);

  return (
    <ApproachSection ref={ref} id="approach">
      <ParticleCanvas id="particle-canvas" />
      <TimelinePattern />
      
      <GlowingCircle
        style={{
          width: '600px',
          height: '600px',
          top: '20%',
          left: '-10%',
          opacity: 0.4
        }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.4, 0.2, 0.4],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut'
        }}
      />
      
      <GlowingCircle
        style={{
          width: '400px',
          height: '400px',
          bottom: '10%',
          right: '-5%',
          opacity: 0.3
        }}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut'
        }}
      />
      
      <Container>
        <SectionContent>
          <Box display="flex" flexDirection="column" alignItems="center">
            <SectionTitle
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              Our Approach
            </SectionTitle>
            <SectionSubtitle
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              A systematic methodology that ensures successful implementation and long-term value creation through our advanced technological solutions.
            </SectionSubtitle>
          </Box>
          
          <ApproachTimeline>
            <TimelineProgress style={{ scaleY }} />
            
            <TimelineItem>
              <TechPattern
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 0.1 }}
                transition={{ duration: 1 }}
                viewport={{ once: true }}
              />
              <ServiceListRight>
                <ServiceItemRight
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <ServiceIconRight>
                    <BarChartIcon />
                  </ServiceIconRight>
                  <ServiceTextRight>
                    <motion.span
                      initial={{ width: 0 }}
                      whileInView={{ width: "100%" }}
                      transition={{ 
                        duration: 1,
                        delay: 0.4,
                        ease: "easeInOut"
                      }}
                      viewport={{ once: true }}
                      style={{
                        display: "block",
                        overflow: "hidden"
                      }}
                    >
                      Business analysis
                    </motion.span>
                  </ServiceTextRight>
                </ServiceItemRight>
                <ServiceItemRight
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  <ServiceIconRight>
                    <DataObjectIcon />
                  </ServiceIconRight>
                  <ServiceTextRight>
                    <motion.span
                      initial={{ width: 0 }}
                      whileInView={{ width: "100%" }}
                      transition={{ 
                        duration: 1,
                        delay: 0.8,
                        ease: "easeInOut"
                      }}
                      viewport={{ once: true }}
                      style={{
                        display: "block",
                        overflow: "hidden"
                      }}
                    >
                      Technical discovery
                    </motion.span>
                  </ServiceTextRight>
                </ServiceItemRight>
                <ServiceItemRight
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  viewport={{ once: true }}
                >
                  <ServiceIconRight>
                    <RouteIcon />
                  </ServiceIconRight>
                  <ServiceTextRight>
                    <motion.span
                      initial={{ width: 0 }}
                      whileInView={{ width: "100%" }}
                      transition={{ 
                        duration: 1,
                        delay: 1.2,
                        ease: "easeInOut"
                      }}
                      viewport={{ once: true }}
                      style={{
                        display: "block",
                        overflow: "hidden"
                      }}
                    >
                      Strategic roadmapping
                    </motion.span>
                  </ServiceTextRight>
                </ServiceItemRight>
              </ServiceListRight>
              <IconContainer>
                <TimelineIcon
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <SearchIcon />
                </TimelineIcon>
              </IconContainer>
              <TimelineContentLeft
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <TimelineConnector isLeft
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                />
                <TimelineTitle variant="h3">
                  Discovery & Strategy
                </TimelineTitle>
                <TimelineDescription>
                  We begin by deeply understanding your business challenges, goals, and existing technology landscape. Our collaborative approach ensures alignment between technological possibilities and business objectives.
                </TimelineDescription>
                <Metric>
                  <span>95%</span> of clients report better strategic alignment after our discovery phase
                </Metric>
              </TimelineContentLeft>
            </TimelineItem>
            
            <TimelineItem>
              <TechPattern
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 0.1 }}
                transition={{ duration: 1 }}
                viewport={{ once: true }}
              />
              <ServiceListLeft>
                <ServiceItemLeft
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <ServiceIconRight>
                    <ArchitectureIcon />
                  </ServiceIconRight>
                  <ServiceTextRight>
                    <motion.span
                      initial={{ width: 0 }}
                      whileInView={{ width: "100%" }}
                      transition={{ 
                        duration: 1,
                        delay: 0.4,
                        ease: "easeInOut"
                      }}
                      viewport={{ once: true }}
                      style={{
                        display: "block",
                        overflow: "hidden"
                      }}
                    >
                      System architecture
                    </motion.span>
                  </ServiceTextRight>
                </ServiceItemLeft>
                <ServiceItemLeft
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  <ServiceIconRight>
                    <SecurityIcon />
                  </ServiceIconRight>
                  <ServiceTextRight>
                    <motion.span
                      initial={{ width: 0 }}
                      whileInView={{ width: "100%" }}
                      transition={{ 
                        duration: 1,
                        delay: 0.8,
                        ease: "easeInOut"
                      }}
                      viewport={{ once: true }}
                      style={{
                        display: "block",
                        overflow: "hidden"
                      }}
                    >
                      Security planning
                    </motion.span>
                  </ServiceTextRight>
                </ServiceItemLeft>
                <ServiceItemLeft
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  viewport={{ once: true }}
                >
                  <ServiceIconRight>
                    <ScaleIcon />
                  </ServiceIconRight>
                  <ServiceTextRight>
                    <motion.span
                      initial={{ width: 0 }}
                      whileInView={{ width: "100%" }}
                      transition={{ 
                        duration: 1,
                        delay: 1.2,
                        ease: "easeInOut"
                      }}
                      viewport={{ once: true }}
                      style={{
                        display: "block",
                        overflow: "hidden"
                      }}
                    >
                      Scalability design
                    </motion.span>
                  </ServiceTextRight>
                </ServiceItemLeft>
              </ServiceListLeft>
              <IconContainer>
                <TimelineIcon>
                  <ArchitectureIcon />
                </TimelineIcon>
              </IconContainer>
              <TimelineContentRight
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <TimelineConnector
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                />
                <TimelineTitle variant="h3">
                  Solution Design
                </TimelineTitle>
                <TimelineDescription>
                  Our expert team designs comprehensive solutions that leverage cutting-edge technologies while ensuring scalability, security, and seamless integration with your existing systems.
                </TimelineDescription>
                <Metric>
                  <span>40%</span> average reduction in implementation time with our design-first approach
                </Metric>
              </TimelineContentRight>
            </TimelineItem>
            
            <TimelineItem>
              <TechPattern
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 0.1 }}
                transition={{ duration: 1 }}
                viewport={{ once: true }}
              />
              <ServiceListRight>
                <ServiceItemRight
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <ServiceIconRight>
                    <DeveloperModeIcon />
                  </ServiceIconRight>
                  <ServiceTextRight>
                    <motion.span
                      initial={{ width: 0 }}
                      whileInView={{ width: "100%" }}
                      transition={{ 
                        duration: 1,
                        delay: 0.4,
                        ease: "easeInOut"
                      }}
                      viewport={{ once: true }}
                      style={{
                        display: "block",
                        overflow: "hidden"
                      }}
                    >
                      Agile development
                    </motion.span>
                  </ServiceTextRight>
                </ServiceItemRight>
                <ServiceItemRight
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  <ServiceIconRight>
                    <VerifiedIcon />
                  </ServiceIconRight>
                  <ServiceTextRight>
                    <motion.span
                      initial={{ width: 0 }}
                      whileInView={{ width: "100%" }}
                      transition={{ 
                        duration: 1,
                        delay: 0.8,
                        ease: "easeInOut"
                      }}
                      viewport={{ once: true }}
                      style={{
                        display: "block",
                        overflow: "hidden"
                      }}
                    >
                      Quality assurance
                    </motion.span>
                  </ServiceTextRight>
                </ServiceItemRight>
                <ServiceItemRight
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  viewport={{ once: true }}
                >
                  <ServiceIconRight>
                    <SyncIcon />
                  </ServiceIconRight>
                  <ServiceTextRight>
                    <motion.span
                      initial={{ width: 0 }}
                      whileInView={{ width: "100%" }}
                      transition={{ 
                        duration: 1,
                        delay: 1.2,
                        ease: "easeInOut"
                      }}
                      viewport={{ once: true }}
                      style={{
                        display: "block",
                        overflow: "hidden"
                      }}
                    >
                      Continuous integration
                    </motion.span>
                  </ServiceTextRight>
                </ServiceItemRight>
              </ServiceListRight>
              <IconContainer>
                <TimelineIcon>
                  <IntegrationInstructionsIcon />
                </TimelineIcon>
              </IconContainer>
              <TimelineContentLeft
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <TimelineConnector isLeft
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                />
                <TimelineTitle variant="h3">
                  Implementation & Integration
                </TimelineTitle>
                <TimelineDescription>
                  We bring concepts to reality through agile development methodologies, ensuring rapid deployment while maintaining quality and security standards throughout the process.
                </TimelineDescription>
                <Metric>
                  <span>99.9%</span> system reliability achieved across implementations
                </Metric>
              </TimelineContentLeft>
            </TimelineItem>
            
            <TimelineItem>
              <TechPattern
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 0.1 }}
                transition={{ duration: 1 }}
                viewport={{ once: true }}
              />
              <ServiceListLeft>
                <ServiceItemLeft
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <ServiceIconRight>
                    <MonitorIcon />
                  </ServiceIconRight>
                  <ServiceTextRight>
                    <motion.span
                      initial={{ width: 0 }}
                      whileInView={{ width: "100%" }}
                      transition={{ 
                        duration: 1,
                        delay: 0.4,
                        ease: "easeInOut"
                      }}
                      viewport={{ once: true }}
                      style={{
                        display: "block",
                        overflow: "hidden"
                      }}
                    >
                      Performance monitoring
                    </motion.span>
                  </ServiceTextRight>
                </ServiceItemLeft>
                <ServiceItemLeft
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  <ServiceIconRight>
                    <NewReleasesIcon />
                  </ServiceIconRight>
                  <ServiceTextRight>
                    <motion.span
                      initial={{ width: 0 }}
                      whileInView={{ width: "100%" }}
                      transition={{ 
                        duration: 1,
                        delay: 0.8,
                        ease: "easeInOut"
                      }}
                      viewport={{ once: true }}
                      style={{
                        display: "block",
                        overflow: "hidden"
                      }}
                    >
                      Feature enhancement
                    </motion.span>
                  </ServiceTextRight>
                </ServiceItemLeft>
                <ServiceItemLeft
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  viewport={{ once: true }}
                >
                  <ServiceIconRight>
                    <UpdateIcon />
                  </ServiceIconRight>
                  <ServiceTextRight>
                    <motion.span
                      initial={{ width: 0 }}
                      whileInView={{ width: "100%" }}
                      transition={{ 
                        duration: 1,
                        delay: 1.2,
                        ease: "easeInOut"
                      }}
                      viewport={{ once: true }}
                      style={{
                        display: "block",
                        overflow: "hidden"
                      }}
                    >
                      Technology updates
                    </motion.span>
                  </ServiceTextRight>
                </ServiceItemLeft>
              </ServiceListLeft>
              <IconContainer>
                <TimelineIcon>
                  <AutorenewIcon />
                </TimelineIcon>
              </IconContainer>
              <TimelineContentRight
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <TimelineConnector
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                />
                <TimelineTitle variant="h3">
                  Continuous Evolution
                </TimelineTitle>
                <TimelineDescription>
                  Our partnerships extend beyond initial implementation with ongoing optimization, enhancement, and adaptation as technologies advance and business needs evolve.
                </TimelineDescription>
                <Metric>
                  <span>85%</span> of clients achieve ROI within the first year
                </Metric>
              </TimelineContentRight>
            </TimelineItem>
          </ApproachTimeline>
        </SectionContent>
      </Container>
    </ApproachSection>
  );
};

export default Approach; 